/**
 * Services Business Logic Hooks
 *
 * TanStack Query hooks for service management (service subcategories, etc.)
 * Optimized for caching with moderate refresh intervals
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getShopServicesApi, createServiceSubcategoryApi } from './services.api';

// Query Keys for business services domain
export const businessServicesKeys = {
    all: ['business', 'services'],
    servicesList: (params) => [...businessServicesKeys.all, 'list', params],
};

/**
 * Hook to get shop services/service subcategories
 *
 * Uses optimized caching for service data:
 * - 5-minute cache (frequently updated data)
 * - Auto refresh on window focus
 * - Background refetch for better UX
 *
 * @param {object} params - Query parameters for filtering/pagination
 * @param {object} options - Query options
 * @returns {object} Query result with services data
 */
export const useGetShopServices = (params = {}, options = {}) => {
    return useQuery({
        queryKey: businessServicesKeys.servicesList(params),
        queryFn: () => getShopServicesApi(params),
        ...options,
    });
};

/**
 * Hook to create a new service subcategory
 *
 * Automatically invalidates and refetches the services list after creation
 * Shows success/error notifications based on mutation result
 *
 * @param {object} options - Mutation options
 * @returns {object} Mutation result with create function
 */
export const useCreateServiceSubcategory = (options = {}) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: createServiceSubcategoryApi,
        ...options,
    });
};
